class HeroBanner extends HTMLElement {
  constructor() {
    super();
    this.shouldCalcHeight = this.classList.contains('h-screen');
    if(this.shouldCalcHeight) {
      this.setHeight(); // Full height needs adjustment
      window.addEventListener('resize', () => {
        this.setHeight();
      });
    }
    this.maxHeight = 0;
    this.autoSlide = (this.hasAttribute('auto-slide') && this.getAttribute('auto-slide') !== 'false');
    this.slideSpeed = parseInt(this.getAttribute('slide-speed')) ?? 10;
    this.totalSlides = this.querySelectorAll('hero-banner-slide').length;
    this.selectedIndex = 0;
    this.isChanging = false;
    if(this.totalSlides > 1) {
      // Setup the automatic slider.
      window.setTimeout(() => this.setSelectedCounter(), 100);
      this.setAuto();
    }
    this.defineCustomEvents();
  }

  defineCustomEvents() {
    this.slideChangeEvent = new CustomEvent('hero-banner:slide:changed', {
      bubbles: true,
      cancelable: true
    });
  }

  setHeight() {
    const top = window.scrollY + this.getBoundingClientRect().top;
    this.style.minHeight = `calc(100vh - ${top}px)`;
  }

  setMaxHeight(height) {
    if(height > this.maxHeight) {
      this.style.height = `${height}px`;
    }
    this.maxHeight = height;
  }

  setSelectedCounter() {
    for(const[,i] of Object.entries(this.querySelectorAll('hero-banner-counter'))) {
      parseInt(i.dataset.index) === this.selectedIndex ? i.setActive() : i.setInactive();
    }
  }

  getCounter(index) {
    return this.querySelector(`hero-banner-counter[data-index="${index}"]`);
  }

  getSlide(index) {
    return this.querySelector(`hero-banner-slide[data-index="${index}"]`);
  }
  
  selectSlide(index) {
    if(this.selectedIndex === parseInt(index)) {
      return true;
    }
    if(this.isChanging) {
      return false;
    }
    this.isChanging = true;
    this.getSlide(this.selectedIndex).toggle();
    this.selectedIndex = parseInt(index);
    this.setSelectedCounter();
    const nextSlide = this.getSlide(index);
    nextSlide.toggle();
    nextSlide.isVideo ? this.clearAuto() : this.resetAuto();
    this.isChanging = false;
    this.slideChangeEvent.activeSlide = nextSlide;
    document.dispatchEvent(this.slideChangeEvent);
  }

  setAuto() {
    if (!Shopify.designMode && this.autoSlide && this.isInView()) {
      this.auto = setTimeout(() => {
        this.nextSlide()
      }, this.slideSpeed * 1000);
    }
  }

  resetAuto() {
    if(!this.isInView(this)) {
      const observer = new IntersectionObserver(function(entries) {
        if(entries[0].isIntersecting === true) {
          entries[0]['target'].clearAuto();
          entries[0]['target'].setAuto();
        }
      }, { threshold: [0]});
      observer.observe(this);
    }
    else {
      if (!Shopify.designMode && this.autoSlide) {
        this.clearAuto();
        this.setAuto();
      }
    }
  }

  clearAuto() {
    clearTimeout(this.auto);
  }

  getNextIndex() {
    return this.selectedIndex === (this.totalSlides - 1) ? 0 : this.selectedIndex + 1;
  }

  getPreviousIndex() {
    return this.selectSlide === 0 ? this.totalSlides - 1 : this.selectedIndex - 1;
  }

  nextSlide() {
    this.selectSlide(this.getNextIndex());
  }

  previousSlide() {
    this.selectSlide(this.getPreviousIndex());
  }

  isInView() {
    const rect = this.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }
}

class HeroBannerSlide extends HTMLElement {
  constructor() {
    super();
    this.container = this.closest('hero-banner');
    this.index = this.dataset.index;
    this.isActive = this.dataset.index === '0';
    this.isVideo = this.querySelector('video') ?? false;
    if(this.isVideo) {
      // Keep track of the number of plays to avoid skipping the video on initial load.
      this.plays = 0;
      this.slideAfterPlay = this.container.totalSlides > 1;
      this.setUpVideoEvents();
    }
    if(this.container.shouldCalcHeight) {
      this.calcHeight();
      window.addEventListener('resize', () => {
        this.calcHeight();
      });
    }
  }

  setUpVideoEvents() {
    this.isVideo.addEventListener("playing", e => {
      if(this.slideAfterPlay && this.plays > 0) {
        this.container.nextSlide();
        return false;
      }
      this.plays++;
    });
  }

  calcHeight() {
    const clone = this.querySelector('[hero-slide-content]').cloneNode(true);
    Object.assign(clone.style, {
      overflow: 'visible',
      height: 'auto',
      maxHeight: 'none',
      opacity: '0',
      visibility: 'hidden',
      display: 'block'
    });
    this.querySelector('[hero-slide-content]').after(clone);
    this.height = clone.offsetHeight;
    this.container.setMaxHeight(this.height);
    clone.remove();
  }

  setActive() {
    this.container.selectSlide(this.dataset.index);
  }

  resetPlayCounter() {
    if(this.isVideo) {
      this.plays = 0;
    }
  }

  toggle() {
    if(this.isActive) {
      this.outOfView();
      this.isActive = false;
    }
    else {
      this.intoView();
      this.isActive = true;
    }
  }

  intoView() {
    this.style.visibility = 'visible';
    this.style.opacity = '1';
    this.style.transform = "translateX(0)";
  }

  outOfView() {
    this.style.transform = "translateX(-100%)";
    this.style.visibility = 'hidden';
    this.style.opacity = '0';
    this.resetPlayCounter();
  }
}

class HeroBannerCounter extends HTMLElement {
  constructor() {
    super();
    this.index = this.dataset.index;
    this.state = this.dataset.state;
    this.container = this.closest('hero-banner');
    this.isActive = false;
    this.addEventListener('click', this.trigger.bind(this));
  }

  trigger() {
    if(!this.isActive) {
      this.container.selectSlide(this.index);
    }
  }

  setActive() {
    this.isActive = true;
    this.setAttribute('data-state', 'active');
  }

  setInactive() {
    this.isActive = false;
    this.removeAttribute('data-state');
  }

}

customElements.define('hero-banner', HeroBanner);
customElements.define('hero-banner-slide', HeroBannerSlide);
customElements.define('hero-banner-counter', HeroBannerCounter);

document.addEventListener('shopify:block:select', function(e) {
  if(e.target.nodeName === 'HERO-BANNER-SLIDE') {
    e.target.setActive();
  }
})